/**
 * SAB-288 Phase 3 — Bonuses & Survivor commissioner-settings types + mock data.
 *
 * Phase 3 ships UI only with mock data; Phase 4 wires it to the live edge
 * functions (apply-default-bonuses-on-league-creation, award-manual-bonus)
 * and the league_bonuses / league_survivor_config tables.
 *
 * Amounts are NUMERIC dollars throughout to match the migration shape
 * (nfl_leagues.prize_pool, league_bonuses.amount). The prototype's
 * amountCents convention is intentionally NOT used here.
 */

export type TriggerType =
  | 'auto_weekly_high'
  | 'auto_season_high'
  | 'auto_survivor'
  | 'manual';

export type BonusFrequency = 'per_week' | 'per_season' | 'one_time';

export type PresetTag = 'classic' | 'inverted' | 'sabotage';

export interface LeagueBonus {
  id: string;
  bonus_name: string;
  description: string;
  amount: number;
  trigger_type: TriggerType;
  frequency: BonusFrequency;
  preset_key: string | null;
  is_default: boolean;
  icon: string;
}

export interface PresetBonus {
  preset_key: string;
  name: string;
  description: string;
  suggested: number;
  frequency: BonusFrequency;
  icon: string;
  tag: PresetTag;
}

export interface SurvivorConfig {
  enabled: boolean;
  start_week: number;
  end_week: number;
  bonus_amount: number;
}

export interface MockTeam {
  id: string;
  name: string;
  owner: string;
  avatar: string;
  color: string;
}

// SAB-632 STEP 0 / LAUNCH GATE (b), SPLIT 2026-08-22.
//
// This was ONE flag covering weekly and survivor together, because neither could be PAID.
// That is now true of only one of them, so the flag splits rather than flipping — flipping it
// would have unblocked survivor by accident, and survivor is ONE-WAY.
//
// Each gate below is mirrored by a server guard in set_prize_schedule. The client is
// convenience; the server is the actual control. Never rely on this file alone.
//
// WEEKLY — UNBLOCKED. evaluate-weekly-bonuses accepts the cron shape and pays (proven
// behaviourally: real wallets credited, re-run refused, different-winner refused), and
// bonus_awards_unique_auto_credit makes a rescore-changed winner refuse rather than
// double-pay. The matching server raise was removed on 2026-08-22.
//
// ⚠ ONE-WAY. Once a league LOCKS with a weekly award disclosed we own paying it, and the
// block cannot be re-applied to that league — the prize schedule freezes at draft completion
// and both freeze guards cover INSERT/DELETE/UPDATE. Setting this back to true only affects
// leagues that have not locked yet.
export const WEEKLY_BLOCKED = false;

// SURVIVOR — STILL BLOCKED, pending SAB-763. The prize is disclosed with the tiebreaker
// "Last team standing; ties to fewest points against", and that tiebreaker is NOT implemented
// for the end-of-window case — the old code paid whichever team came first out of an
// unordered select. Allowing a commissioner to disclose a rule the payout engine does not
// honour is a false disclosure under A-1, independent of whether any particular payout is
// wrong. The server raise (survivor_blocked_until_sab648) is deliberately still in place.
export const SURVIVOR_BLOCKED = true;

export const SURVIVOR_BLOCK_NOTE =
  'Temporarily unavailable — the payout tiebreaker is not yet implemented.';
// Only rendered if WEEKLY_BLOCKED is set back to true; kept so the weekly gate stays usable.
export const WEEKLY_BLOCK_NOTE =
  'Temporarily unavailable — automatic payout is not yet enabled.';

/** Full preset library from data.jsx, translated to NUMERIC dollars. */
export const PRESET_LIBRARY: PresetBonus[] = [
  { preset_key: 'weekly_high',        name: 'Highest Weekly Score',         icon: 'trending-up',   suggested: 10, frequency: 'per_week',   description: 'Most fantasy points this week.', tag: 'classic' },
  { preset_key: 'weekly_pa',          name: 'Most Points Against',          icon: 'shield-off',    suggested: 5,  frequency: 'per_week',   description: 'Got smoked the hardest. Punish prize.', tag: 'inverted' },
  { preset_key: 'weekly_low',         name: 'Lowest Weekly Score',          icon: 'trending-down', suggested: 5,  frequency: 'per_week',   description: 'Trash mode bonus. The worst weekly performance.', tag: 'inverted' },
  { preset_key: 'weekly_turnovers',   name: 'Most Turnovers',               icon: 'rotate-ccw',    suggested: 3,  frequency: 'per_week',   description: 'Fumbles + interceptions across all starters.', tag: 'inverted' },
  { preset_key: 'weekly_sacks_taken', name: 'Most Sacks Allowed',           icon: 'shield-x',      suggested: 3,  frequency: 'per_week',   description: "Your QBs got demolished. Here's a check.", tag: 'inverted' },
  { preset_key: 'weekly_missed_fg',   name: 'Most Missed Field Goals',      icon: 'target',        suggested: 2,  frequency: 'per_week',   description: 'Your kicker single-handedly cost you.', tag: 'inverted' },
  { preset_key: 'weekly_bench_high',  name: 'Highest Bench Score',          icon: 'sofa',          suggested: 4,  frequency: 'per_week',   description: "Best lineup you didn't start. Coach of the week.", tag: 'classic' },
  { preset_key: 'weekly_blowout',     name: 'Biggest Blowout Win',          icon: 'zap',           suggested: 5,  frequency: 'per_week',   description: 'Largest margin of victory in any matchup.', tag: 'classic' },
  { preset_key: 'weekly_closest',     name: 'Closest Win',                  icon: 'git-merge',     suggested: 3,  frequency: 'per_week',   description: 'Won by the smallest margin.', tag: 'classic' },
  { preset_key: 'season_top_week',    name: 'Highest Single Week',          icon: 'flame',         suggested: 50, frequency: 'per_season', description: 'Best one-week explosion of the season.', tag: 'classic' },
  { preset_key: 'season_low_week',    name: 'Lowest Single Week',           icon: 'frown',         suggested: 25, frequency: 'per_season', description: 'The single worst weekly performance.', tag: 'inverted' },
  { preset_key: 'season_pf',          name: 'Most Points For (Season)',     icon: 'crown',         suggested: 50, frequency: 'per_season', description: 'Highest cumulative fantasy points all season.', tag: 'classic' },
  { preset_key: 'season_pa',          name: 'Most Points Against (Season)', icon: 'shield',        suggested: 30, frequency: 'per_season', description: 'Punching bag of the year. Bruise prize.', tag: 'inverted' },
  { preset_key: 'season_streak',      name: 'Longest Losing Streak',        icon: 'arrow-down',    suggested: 25, frequency: 'per_season', description: 'Tank champion. 5+ Ls in a row pays.', tag: 'inverted' },
  { preset_key: 'season_sabotaged',   name: 'Most Sabotaged Player',        icon: 'bomb',          suggested: 20, frequency: 'per_season', description: 'The single player hit by the most sabotage cards.', tag: 'sabotage' },
  { preset_key: 'season_sab_used',    name: 'Most Sabotage Cards Played',   icon: 'flame',         suggested: 20, frequency: 'per_season', description: 'The chaos engine of the league.', tag: 'sabotage' },
];

/**
 * Static fallback used as the default `teams` arg for AwardDialog when no
 * caller passes a real list. Live UI in NFLBonusesSection always passes
 * the real league teams; this fallback exists for storybook/tests.
 */
export const MOCK_TEAMS: MockTeam[] = [
  { id: 't1',  name: 'Trash Pandas',    owner: 'You',     avatar: 'TP', color: '#F97316' },
  { id: 't2',  name: 'Wreck Crew',      owner: 'mike_h',  avatar: 'WC', color: '#EF4444' },
  { id: 't3',  name: 'Bad News Bears',  owner: 'sarah_q', avatar: 'BB', color: '#22C55E' },
  { id: 't4',  name: 'Bench Warmers',   owner: 'tim_v',   avatar: 'BW', color: '#3B82F6' },
  { id: 't5',  name: 'The Saboteurs',   owner: 'jp',      avatar: 'TS', color: '#A855F7' },
  { id: 't6',  name: 'No Hopes',        owner: 'kev',     avatar: 'NH', color: '#EAB308' },
  { id: 't7',  name: 'Dumpster Fire',   owner: 'ari_m',   avatar: 'DF', color: '#EC4899' },
  { id: 't8',  name: 'Punt Returns',    owner: 'nico',    avatar: 'PR', color: '#14B8A6' },
  { id: 't9',  name: 'Last Pick',       owner: 'beth_z',  avatar: 'LP', color: '#F472B6' },
  { id: 't10', name: 'Total Tankers',   owner: 'dom',     avatar: 'TT', color: '#94A3B8' },
];

export function fmtUSD(n: number): string {
  return '$' + n.toLocaleString('en-US', {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}
